var _excluded = ["overscanCount", "items", "renderItem", "onRowsRendered"];

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import { useForwardedRef } from '@adsk/alloy-react-helpers';

/**
 * A Virtualized List component, that allows generic scrolling functionality for large lists.
 */
var VirtualizedListWrapped = function VirtualizedListWrapped(_ref, ref) {
  var overscanCount = _ref.overscanCount,
      _ref$items = _ref.items,
      items = _ref$items === void 0 ? [] : _ref$items,
      renderItem = _ref.renderItem,
      _onRowsRendered = _ref.onRowsRendered,
      props = _objectWithoutProperties(_ref, _excluded);

  var cellMesureCacheRef = useRef(new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 10
  }));
  var listRef = useForwardedRef(ref); // Wrapper for row rendering. takes care of cell-measurement and applying the style needed for react-virtualized.

  var rowRenderer = useCallback(function (_ref2) {
    var index = _ref2.index,
        key = _ref2.key,
        parent = _ref2.parent,
        style = _ref2.style;
    return /*#__PURE__*/React.createElement(CellMeasurer, {
      cache: cellMesureCacheRef.current,
      columnIndex: 0,
      key: key,
      parent: parent,
      rowIndex: index
    }, function (_ref3) {
      var registerChild = _ref3.registerChild;
      return /*#__PURE__*/React.createElement("div", {
        ref: registerChild,
        style: style
      }, renderItem === null || renderItem === void 0 ? void 0 : renderItem({
        item: items[index],
        index: index
      }));
    });
  }, [items, renderItem]); // A hack suggested by Dan Abramov, in order to do a deep compare when using useEffect:
  // https://github.com/facebook/react/issues/14476#issuecomment-471199055

  var itemsString = JSON.stringify(items); // Equivalent to componentWillReceiveProps

  useEffect(function () {
    cellMesureCacheRef.current.clearAll();

    if (listRef.current) {
      listRef.current.forceUpdateGrid();
      listRef.current.recomputeRowHeights();
    }
  }, [itemsString, listRef]);
  return /*#__PURE__*/React.createElement(AutoSizer, null, function (_ref4) {
    var height = _ref4.height,
        width = _ref4.width;
    return /*#__PURE__*/React.createElement(List, _extends({
      ref: listRef,
      style: {
        outline: 'none'
      } // Needed to remove the default blue outline of the list.
      ,
      rowRenderer: rowRenderer,
      overscanRowCount: overscanCount,
      deferredMeasurementCache: cellMesureCacheRef.current,
      width: width,
      height: height,
      rowHeight: cellMesureCacheRef.current.rowHeight,
      rowCount: items.length,
      items: items,
      onRowsRendered: function onRowsRendered(_ref5) {
        var startIndex = _ref5.startIndex,
            stopIndex = _ref5.stopIndex;

        if (_onRowsRendered) {
          _onRowsRendered({
            cellMesureCacheRef: cellMesureCacheRef,
            items: items,
            startIndex: startIndex,
            stopIndex: stopIndex
          });
        }
      }
    }, props));
  });
};

var VirtualizedList = /*#__PURE__*/React.forwardRef(VirtualizedListWrapped);
VirtualizedList.displayName = 'VirtualizedList';
VirtualizedList.propTypes = {
  /**  Items array. Every item should be an object with only metadata - not a ReactNode! */
  items: PropTypes.array,

  /**  Item renderer - a callback with the following signature: ({ item, index }) */
  renderItem: PropTypes.func.isRequired,

  /**  Number of rows to render above/below the visible bounds of the list */
  overscanCount: PropTypes.number,

  /** Optional callback that's being called after the rows are being rendered. Signature: { cellMesureCacheRef, items, startIndex, stopIndex } */
  onRowsRendered: PropTypes.func
};
export default VirtualizedList;
//# sourceMappingURL=VirtualizedList.js.map